/**
 * Per-invoice session ledger.
 *
 * Shows the exact arithmetic behind the patient's headline "bought / used" counter, grouped by the
 * bill the sessions were purchased on rather than pooled by service. Staff think in terms of bills
 * ("has the July package been used up yet?"), so the breakdown is organised the same way.
 *
 * Three things this view deliberately surfaces rather than hides:
 *  - FIFO allocation. A completed visit records only a procedure, never a specific bill, so the
 *    server allocates consumption oldest-invoice-first. An older bill therefore settles at zero and
 *    stays there once drawn down.
 *  - Negative balances. A service on the active bill can be booked past its own balance by borrowing
 *    against the patient's global pool, so over-usage is a real state that must stay visible. It is
 *    parked on the newest bill holding that service, showing which bill incurred the extra usage.
 *  - Unattributed history. Visits completed before procedures were tracked belong to no invoice;
 *    they are reported separately so the per-bill rows still reconcile with the global totals.
 */
import React from 'react';
import { X, AlertTriangle, Receipt } from 'lucide-react';
import { useGetPatientSessionLedgerQuery } from '../../api/patientsApi';

const fmtINR = (v) =>
  v == null ? '—' : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function RemainingCell({ value }) {
  const negative = value < 0;
  return (
    <span
      style={{
        fontWeight: negative ? 800 : 600,
        color: negative ? 'var(--red-700)' : value === 0 ? 'var(--text-muted)' : 'var(--green-700)',
        whiteSpace: 'nowrap',
      }}
    >
      {negative && <AlertTriangle size={12} style={{ marginRight: 4, verticalAlign: '-2px' }} />}
      {value}
      {negative && <span style={{ fontWeight: 600 }}> over</span>}
    </span>
  );
}

function InvoiceGroup({ group }) {
  const overUsed = group.items.filter((i) => i.remaining < 0);
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '8px 10px', borderRadius: '8px 8px 0 0',
          background: 'var(--gray-100)', border: '1px solid var(--border)', borderBottom: 'none',
        }}
      >
        <Receipt size={14} style={{ color: 'var(--text-muted)' }} />
        <strong style={{ fontSize: '0.85rem' }}>{group.invoiceNumber}</strong>
        <span className="td-muted" style={{ fontSize: '0.75rem' }}>{fmtDate(group.issuedAt)}</span>
        {group.isLatest && (
          <span className="badge b-blue" style={{ marginLeft: 'auto', fontSize: '0.68rem' }}>
            Active bill
          </span>
        )}
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '0 0 8px 8px' }}>
        <table className="table" style={{ width: '100%', fontSize: '0.85rem', margin: 0 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Procedure</th>
              <th style={{ textAlign: 'right' }}>Bought</th>
              <th style={{ textAlign: 'right' }}>Completed</th>
              <th style={{ textAlign: 'right' }}>Remaining</th>
              <th style={{ textAlign: 'right' }}>Rate</th>
            </tr>
          </thead>
          <tbody>
            {group.items.map((i) => (
              <tr key={i.treatmentTypeId}>
                <td>{i.name}</td>
                <td style={{ textAlign: 'right' }}>{i.bought}</td>
                <td style={{ textAlign: 'right' }}>{i.completed}</td>
                <td style={{ textAlign: 'right' }}><RemainingCell value={i.remaining} /></td>
                <td style={{ textAlign: 'right' }}>{fmtINR(i.unitPrice)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {overUsed.length > 0 && (
        <div className="td-muted" style={{ fontSize: '0.72rem', marginTop: 6, color: 'var(--red-700)' }}>
          Extra usage ran against this bill: {overUsed.map((i) => `${i.name} (${Math.abs(i.remaining)})`).join(', ')}.
        </div>
      )}
    </div>
  );
}

export default function SessionLedgerModal({ patientId, onClose }) {
  const { data, isLoading, isError } = useGetPatientSessionLedgerQuery(patientId, { skip: !patientId });

  const invoices = data?.invoices ?? [];
  const unattributed = data?.unattributedCompleted ?? 0;
  const overUsedItems = (data?.items ?? []).filter((i) => i.remaining < 0 && i.treatmentTypeId);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <h2>Session breakdown</h2>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">
          {isLoading && <p className="td-muted">Loading…</p>}
          {isError && <p style={{ color: 'var(--red-700)' }}>Could not load the session ledger.</p>}

          {!isLoading && !isError && (
            <>
              {overUsedItems.length > 0 && (
                <div
                  style={{
                    display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14,
                    padding: '10px 12px', borderRadius: 8,
                    background: 'var(--red-50, #fdf2f2)', border: '1px solid var(--red-300, #f5c6cb)',
                  }}
                >
                  <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2, color: 'var(--red-700)' }} />
                  <div style={{ fontSize: '0.82rem' }}>
                    <strong>Over-used:</strong>{' '}
                    {overUsedItems.map((i) => `${i.name} (${Math.abs(i.remaining)})`).join(', ')}.
                    {' '}These borrowed from the patient&apos;s remaining balance; a new bill for them settles it.
                  </div>
                </div>
              )}

              {invoices.length === 0 && (
                <p className="td-muted" style={{ textAlign: 'center', padding: '18px 0' }}>
                  Nothing billed yet.
                </p>
              )}

              {invoices.map((g) => <InvoiceGroup key={g.invoiceId} group={g} />)}

              {unattributed > 0 && (
                <div
                  style={{
                    padding: '10px 12px', borderRadius: 8, marginBottom: 14,
                    border: '1px dashed var(--border)',
                  }}
                >
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                    Not attributed to a bill
                  </div>
                  <div className="td-muted" style={{ fontSize: '0.76rem', marginTop: 4 }}>
                    {unattributed} completed {unattributed === 1 ? 'visit has' : 'visits have'} no session type set,
                    so {unattributed === 1 ? 'it belongs' : 'they belong'} to no invoice. Set the session type on the
                    appointment to attribute {unattributed === 1 ? 'it' : 'them'}.
                  </div>
                </div>
              )}

              <div
                style={{
                  display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'flex-end',
                  paddingTop: 12, borderTop: '2px solid var(--border)', fontWeight: 800, fontSize: '0.9rem',
                }}
              >
                <span>Bought: {data?.totalBought ?? 0}</span>
                <span>Completed: {data?.totalCompleted ?? 0}</span>
                <span>Remaining: <RemainingCell value={data?.totalRemaining ?? 0} /></span>
              </div>

              <p className="td-muted" style={{ fontSize: '0.72rem', marginTop: 12 }}>
                Calculated live from bills and completed appointments. Cancelled and no-show visits never
                consume a session. Sessions are drawn from the oldest bill first.
              </p>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
